import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';

const IntentContext = createContext();

const initialState = {
    rules: [],
    logs: [],
    stats: null,
    loading: false,
    error: null,
    pagination: { page: 1, total: 0, pages: 0, limit: 20 }
};

const intentReducer = (state, action) => {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, loading: action.payload, error: null };
        case 'SET_ERROR':
            return { ...state, error: action.payload, loading: false };
        case 'SET_RULES':
            return { 
                ...state, 
                rules: action.payload.data || action.payload,
                pagination: action.payload.pagination || state.pagination,
                loading: false,
                error: null
            };
        case 'ADD_RULE':
            return { 
                ...state, 
                rules: [...state.rules, action.payload],
                loading: false,
                error: null
            };
        case 'UPDATE_RULE':
            return {
                ...state,
                rules: state.rules.map(rule => 
                    rule._id === action.payload._id ? { ...rule, ...action.payload } : rule
                ),
                loading: false,
                error: null
            };
        case 'DELETE_RULE':
            return {
                ...state,
                rules: state.rules.filter(rule => rule._id !== action.payload),
                loading: false,
                error: null
            };
        case 'SET_LOGS':
            return {
                ...state,
                logs: action.payload.data || action.payload,
                pagination: action.payload.pagination || state.pagination,
                loading: false,
                error: null
            };
        case 'SET_STATS':
            return {
                ...state,
                stats: action.payload,
                loading: false,
                error: null
            };
        case 'UPDATE_RULE_STATS':
            return {
                ...state,
                rules: state.rules.map(rule => {
                    if (rule._id === action.payload.ruleId) {
                        const updatedStats = { ...rule.stats };
                        if (action.payload.success) {
                            updatedStats.successfulTriggers = (updatedStats.successfulTriggers || 0) + 1;
                        }
                        updatedStats.totalTriggers = (updatedStats.totalTriggers || 0) + 1;
                        updatedStats.lastTriggered = new Date().toISOString();
                        // Update average confidence
                        const currentAvg = updatedStats.avgConfidence || 0;
                        const total = updatedStats.totalTriggers;
                        updatedStats.avgConfidence = ((currentAvg * (total - 1)) + action.payload.confidence) / total;
                        
                        return { ...rule, stats: updatedStats };
                    }
                    return rule;
                })
            };
        default:
            return state;
    }
};

export const IntentProvider = ({ children }) => {
    const [state, dispatch] = useReducer(intentReducer, initialState);

    // Fetch intent rules
    const fetchRules = useCallback(async (params = {}) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const response = await apiService.getIntentRules(params);
            dispatch({ type: 'SET_RULES', payload: response.data });
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: error.message });
            console.error('Failed to fetch intent rules:', error);
        }
    }, []);

    // Get single rule
    const getRule = useCallback(async (id) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const response = await apiService.getIntentRule(id);
            return response.data;
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: error.message });
            throw error;
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, []);

    // Create new rule
    const createRule = useCallback(async (ruleData) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const response = await apiService.createIntentRule(ruleData);
            dispatch({ type: 'ADD_RULE', payload: response.data });
            return response.data;
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: error.message });
            throw error;
        }
    }, []);

    // Update existing rule
    const updateRule = useCallback(async (id, ruleData) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const response = await apiService.updateIntentRule(id, ruleData);
            dispatch({ type: 'UPDATE_RULE', payload: response.data });
            return response.data;
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: error.message });
            throw error;
        }
    }, []);

    // Delete rule
    const deleteRule = useCallback(async (id) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            await apiService.deleteIntentRule(id);
            dispatch({ type: 'DELETE_RULE', payload: id });
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: error.message });
            throw error;
        }
    }, []);

    // Toggle rule active status
    const toggleRule = useCallback(async (id) => {
        try {
            const response = await apiService.toggleIntentRule(id);
            dispatch({ type: 'UPDATE_RULE', payload: response.data });
            return response.data;
        } catch (error) {
            toast.error('Failed to toggle intent rule');
            throw error;
        }
    }, []);

    // Duplicate rule
    const duplicateRule = useCallback(async (id) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const response = await apiService.duplicateIntentRule(id);
            dispatch({ type: 'ADD_RULE', payload: response.data });
            return response.data;
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: error.message });
            throw error;
        }
    }, []);

    // Test rule
    const testRule = useCallback(async (id, message) => {
        try {
            const response = await apiService.testIntentRule(id, message);
            return response.data;
        } catch (error) {
            throw error;
        }
    }, []);

    // Generate patterns using AI
    const generatePattern = useCallback(async (description, examples = []) => {
        try {
            const response = await apiService.generatePattern(description, examples);
            return response; // fetchWithAuth already returns the data directly
        } catch (error) {
            throw error;
        }
    }, []);

    // Fetch intent logs
    const fetchLogs = useCallback(async (params = {}) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const response = await apiService.getIntentLogs(params);
            dispatch({ type: 'SET_LOGS', payload: response.data });
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: error.message });
            console.error('Failed to fetch intent logs:', error);
        }
    }, []);

    // Fetch intent statistics
    const fetchStats = useCallback(async (days = 7) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const response = await apiService.getIntentStats(days);
            dispatch({ type: 'SET_STATS', payload: response.data });
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: error.message });
            console.error('Failed to fetch intent stats:', error);
        }
    }, []);

    // Test OpenRouter connection
    const testOpenRouter = useCallback(async () => {
        try {
            const response = await apiService.testOpenRouter();
            return response.data;
        } catch (error) {
            throw error;
        }
    }, []);

    // Update rule stats (used by socket events)
    const updateRuleStats = useCallback((ruleId, statsUpdate) => {
        dispatch({ 
            type: 'UPDATE_RULE_STATS', 
            payload: { ruleId, ...statsUpdate } 
        });
    }, []);

    // Clear error
    const clearError = useCallback(() => {
        dispatch({ type: 'SET_ERROR', payload: null });
    }, []);

    // Reset state
    const resetState = useCallback(() => {
        dispatch({ type: 'SET_RULES', payload: { data: [], pagination: initialState.pagination } });
        dispatch({ type: 'SET_LOGS', payload: { data: [], pagination: initialState.pagination } });
        dispatch({ type: 'SET_STATS', payload: null });
        dispatch({ type: 'SET_ERROR', payload: null });
    }, []);

    const contextValue = {
        // State
        ...state,
        
        // Actions
        fetchRules,
        getRule,
        createRule,
        updateRule,
        deleteRule,
        toggleRule,
        duplicateRule,
        testRule,
        generatePattern,
        fetchLogs,
        fetchStats,
        testOpenRouter,
        updateRuleStats,
        clearError,
        resetState
    };

    return (
        <IntentContext.Provider value={contextValue}>
            {children}
        </IntentContext.Provider>
    );
};

export const useIntent = () => {
    const context = useContext(IntentContext);
    if (!context) {
        throw new Error('useIntent must be used within IntentProvider');
    }
    return context;
};

export default IntentContext;